// ภูมิภาคที่มีปรากฏการณ์ธรรมชาติเฉพาะ (docs/PLAN.md เฟส 3: เหตุการณ์ตามภูมิภาค)
// ต่างจากประเภทพื้นที่ (biomes.ts) ตรงที่ดูจากพิกัด เช่น ป่าดิบชื้นเหมือนกัน แต่แอมะซอนมีน้ำท่วมป่า ส่วนเอเชียมีลมมรสุม
// กรอบพิกัดหยาบๆ [ใต้, ตะวันตก, เหนือ, ตะวันออก] พอให้เหตุการณ์เกิดถูกที่ ไม่ได้ใช้วาดแผนที่

import type { LatLon } from "../engine/grid";

export type RegionId =
  | "monsoon_asia"
  | "typhoon_belt"
  | "hurricane_belt"
  | "cyclone_belt"
  | "amazon"
  | "tonle_sap"
  | "humboldt"
  | "serengeti"
  | "sardine_coast"
  | "arctic_sea"
  | "southern_ocean"
  | "temperate"
  | "cold_north";

type Box = readonly [number, number, number, number];

interface RegionDef {
  nameTh: string;
  realm: "land" | "sea" | "both"; // ใช้กับช่องบนบก ช่องทะเล หรือทั้งสองแบบ
  boxes: readonly Box[];
}

export const REGIONS: Readonly<Record<RegionId, RegionDef>> = {
  monsoon_asia: { nameTh: "เอเชียมรสุม", realm: "both", boxes: [[5, 66, 30, 123], [-10, 95, 5, 125]] },
  typhoon_belt: { nameTh: "แปซิฟิกตะวันตกเฉียงเหนือ", realm: "both", boxes: [[5, 100, 40, 170]] },
  hurricane_belt: { nameTh: "แอตแลนติกเหนือและทะเลแคริบเบียน", realm: "both", boxes: [[10, -100, 40, -45]] },
  cyclone_belt: { nameTh: "มหาสมุทรอินเดียและแปซิฟิกใต้", realm: "both", boxes: [[5, 50, 25, 100], [-30, 40, -5, 175]] },
  amazon: { nameTh: "ลุ่มน้ำแอมะซอน", realm: "land", boxes: [[-15, -75, 5, -45]] },
  tonle_sap: { nameTh: "ทะเลสาบเขมรและแม่น้ำโขงตอนล่าง", realm: "land", boxes: [[10.5, 102, 14.5, 106.5]] },
  humboldt: { nameTh: "กระแสน้ำฮัมโบลต์", realm: "sea", boxes: [[-45, -90, 0, -70]] },
  serengeti: { nameTh: "เซเรงเกติและมาไซมารา", realm: "land", boxes: [[-5, 33, 0, 37]] },
  sardine_coast: { nameTh: "ชายฝั่งตะวันออกของแอฟริกาใต้", realm: "sea", boxes: [[-35, 15, -25, 35]] },
  arctic_sea: { nameTh: "มหาสมุทรอาร์กติก", realm: "sea", boxes: [[66, -180, 90, 180]] },
  southern_ocean: { nameTh: "มหาสมุทรใต้", realm: "sea", boxes: [[-80, -180, -55, 180]] },
  temperate: { nameTh: "เขตอบอุ่น", realm: "land", boxes: [[35, -180, 60, 180], [-60, -180, -35, 180]] },
  cold_north: { nameTh: "ซีกโลกเหนือที่มีฤดูหนาวยาว", realm: "land", boxes: [[40, -180, 72, 180]] },
};

export const REGION_IDS = Object.keys(REGIONS) as RegionId[];

export function inRegion(id: RegionId, at: LatLon, onSea: boolean): boolean {
  const r = REGIONS[id];
  if (r.realm === "land" && onSea) return false;
  if (r.realm === "sea" && !onSea) return false;
  return r.boxes.some(([s, w, n, e]) => at.lat >= s && at.lat <= n && at.lon >= w && at.lon <= e);
}

/** ภูมิภาคทั้งหมดที่ตำแหน่งนี้อยู่ */
export function regionsAt(at: LatLon, onSea: boolean): RegionId[] {
  return REGION_IDS.filter((id) => inRegion(id, at, onSea));
}

/** จุดตัวอย่างในภูมิภาค (กลางกรอบแรก) ให้ตัวจำลองใช้ */
export function regionSample(id: RegionId): LatLon {
  const [s, w, n, e] = REGIONS[id].boxes[0];
  return { lat: (s + n) / 2, lon: (w + e) / 2 };
}
